import { useEffect, useMemo, useState } from "react";
import {
    BookOpenIcon,
    FileTextIcon,
    Loader2Icon,
    SearchIcon,
    AlertCircleIcon,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Checkbox } from "@component/checkbox";
import { listAllKbDocuments, type KbDocument } from "@service/knowledge-base";

function formatFileSize(bytes: number): string {
    if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
    return `${Math.round(bytes / 1_000)} KB`;
}

/**
 * "Attach from Knowledge Base" — picks existing workspace documents to attach
 * to a narrower scope (campaign today, AIQM batch later). Attaching, not
 * copying: the document stays one stored blob; only an attachment is added.
 *
 * Failed documents are listed but disabled — attaching a document whose
 * extraction failed would silently contribute nothing to evaluation, which is
 * exactly the kind of quiet no-op this feature is meant to eliminate.
 */
export function KnowledgePickerDialog({
    open,
    onOpenChange,
    excludeIds = [],
    onAttach,
    attaching = false,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Documents already attached — hidden from the list. */
    excludeIds?: string[];
    onAttach: (docs: KbDocument[]) => void;
    /** Parent-controlled busy state while the attach request is in flight. */
    attaching?: boolean;
}) {
    const [documents, setDocuments] = useState<KbDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // (Re)load whenever the dialog opens so a doc uploaded moments ago shows up.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        setSelectedIds(new Set());
        listAllKbDocuments()
            .then((docs) => {
                if (!cancelled) setDocuments(docs);
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
    const rows = documents.filter((d) => {
        if (excluded.has(d.id)) return false;
        if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    function toggle(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function handleAttach() {
        const docs = documents.filter((d) => selectedIds.has(d.id));
        if (docs.length === 0) return;
        onAttach(docs);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="rounded-full bg-blue-100 p-2">
                            <BookOpenIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle>Attach from Knowledge Base</DialogTitle>
                            <p className="text-xs text-gray-500 mt-0.5">
                                The document stays in your Knowledge Base — this only adds it here.
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="relative flex-shrink-0">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Search documents…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>

                <div className="flex-1 overflow-y-auto min-h-[200px] border border-gray-200 rounded-lg">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <Loader2Icon className="h-6 w-6 text-gray-300 animate-spin" />
                            <p className="text-sm text-gray-500 mt-2">Loading documents…</p>
                        </div>
                    ) : loadError ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                            <AlertCircleIcon className="h-6 w-6 text-gray-300" />
                            <p className="text-sm text-gray-500 mt-2">
                                Couldn't load your Knowledge Base. Close and try again.
                            </p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                            <FileTextIcon className="h-6 w-6 text-gray-300" />
                            <p className="text-sm text-gray-500 mt-2">
                                {search
                                    ? "No documents match your search."
                                    : "No other documents available to attach."}
                            </p>
                        </div>
                    ) : (
                        rows.map((doc, idx) => {
                            const failed = doc.status === "failed" || doc.extractionStatus === "failed";
                            return (
                                <label
                                    key={doc.id}
                                    className={
                                        "flex items-center gap-3 p-3 transition-colors " +
                                        (failed
                                            ? "opacity-50 cursor-not-allowed"
                                            : "hover:bg-gray-50 cursor-pointer") +
                                        (idx !== rows.length - 1 ? " border-b border-gray-100" : "")
                                    }
                                >
                                    <Checkbox
                                        checked={selectedIds.has(doc.id)}
                                        onCheckedChange={() => toggle(doc.id)}
                                        disabled={failed}
                                    />
                                    <FileTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">
                                            {doc.name}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {doc.fileType} · {formatFileSize(doc.sizeBytes)}
                                            {failed && (
                                                <span className="text-rose-500"> · Processing failed</span>
                                            )}
                                        </p>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-1 flex-shrink-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAttach}
                        disabled={selectedIds.size === 0 || attaching}
                    >
                        {attaching
                            ? "Attaching…"
                            : `Attach${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
